import {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  PropsWithChildren,
} from "react";
import type {
  Stream as IStream,
  IUpdateStream,
  IStreamContext,
  ICurrentStream,
} from "~api/stream/interfaces";
import { getStreamContent, saveStream } from "~api/stream";
import { useToastNotificationContext } from "./ToastNotification";
import { useAuth } from "./Auth";

const StreamContext = createContext<IStreamContext>({
  loading: false,
  stream: null,
  currentStream: null,
  updateStream: () => "",
  setCurrentStream: () => "",
  playedSeconds: 0,
  setPlayedSeconds: () => 0,
});

export const StreamContextProvider = ({ children }: PropsWithChildren) => {
  const { setToastData } = useToastNotificationContext();
  const { signed } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [stream, setStream] = useState<IStream | null>(null);
  const [playedSeconds, setPlayedSeconds] = useState<number>(0);
  const previousSignedRef = useRef<boolean | null>(null);

  // Carregar currentStream do localStorage na inicialização
  const [currentStream, setCurrentStream] = useState<ICurrentStream | null>(() => {
    if (typeof window !== "undefined") {
      try {
        const stored = window.localStorage.getItem("current_stream");
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (error) {
        console.error("Error loading currentStream from localStorage:", error);
      }
    }
    return null;
  });

  // Quando o usuário deslogar, limpar currentStream e localStorage
  useEffect(() => {
    // Só limpar se realmente deslogou (signed mudou de true para false)
    // Não limpar na inicialização quando signed ainda é false ou null
    const wasSigned = previousSignedRef.current;
    const hasToken =
      typeof window !== "undefined" && window.localStorage.getItem("@app:token");

    // Se estava logado antes e agora não está, e não há token, então realmente deslogou
    if (wasSigned === true && signed === false && !hasToken && currentStream) {
      // Quando deslogar, limpar tudo
      setCurrentStream(null);
      if (typeof window !== "undefined") {
        window.localStorage.removeItem("current_stream");
      }
    }

    // Atualizar referência para próxima verificação
    previousSignedRef.current = signed;
  }, [signed, currentStream]);

  // Sincronizar currentStream com localStorage quando mudar (apenas quando selecionado manualmente)
  useEffect(() => {
    if (typeof window === "undefined") return;

    if (currentStream && signed) {
      window.localStorage.setItem("current_stream", JSON.stringify(currentStream));
    } else if (!currentStream && signed) {
      // Se currentStream for null mas estiver logado, remover do localStorage
      window.localStorage.removeItem("current_stream");
    }
  }, [currentStream, signed]);

  // Carregar stream quando currentStream for definido
  useEffect(() => {
    if (currentStream) {
      // Clear previous stream to avoid showing stale data while reloading
      setStream(null);
      setLoading(true);
      getStreamContent(currentStream.name)
        .then((response) => {
          // Garantir que sempre temos um objeto stream válido
          if (response && response.id_stream) {
            setStream(response);
          } else {
            // Se a resposta estiver vazia ou inválida, criar um stream padrão
            setStream({
              id_stream: currentStream.id,
              stream_name: currentStream.name,
              brand: response?.brand || {
                id: 0,
                name: currentStream.brand,
                ecommerce_url: "",
                frontend_url: "",
                provider: "",
                provider_url: "",
                search_path: "",
                analytics: "",
              },
              sales_channel: Number(currentStream.sales_channel) || 0,
              is_live: 0,
              has_chat: 0,
              has_reaction: 0,
              has_measure: 0,
              video: {
                url: "",
                vimeo_id: null,
                rerunStartTime: 0,
                isRerun: 0,
                title: "",
              },
              stream_url: "",
              buttons: [],
            });
          }
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error loading stream:", error);
          // Mesmo em caso de erro, criar um stream padrão para permitir configuração
          setStream({
            id_stream: currentStream.id,
            stream_name: currentStream.name,
            brand: {
              id: 0,
              name: currentStream.brand,
              ecommerce_url: "",
              frontend_url: "",
              provider: "",
              provider_url: "",
              search_path: "",
              analytics: "",
            },
            sales_channel: Number(currentStream.sales_channel) || 0,
            is_live: 0,
            has_chat: 0,
            has_reaction: 0,
            has_measure: 0,
            video: {
              url: "",
              vimeo_id: null,
              rerunStartTime: 0,
              isRerun: 0,
              title: "",
            },
            stream_url: "",
            buttons: [],
          });
          setLoading(false);
        });
    } else {
      // Limpar stream quando não há currentStream
      setStream(null);
      setLoading(false);
    }
  }, [currentStream]);

  async function updateStream(data: IUpdateStream) {
    if (!stream || !currentStream) throw new Error("Sem dados da stream");

    return saveStream(data, currentStream.id).then((response) => {
      if (response.status !== 200) {
        throw new Error("Erro ao atualizar a stream");
      } else {
        setToastData(
          {
            text: `as alterações da STREAM ${currentStream.name} foram publicadas`,
            title: "informações publicadas",
          },
          "success"
        );
        setStream({
          ...stream,
          ...{
            is_live: data.is_live,
            has_chat: data.has_chat,
            has_reaction: data.has_reaction,
            has_measure: data.has_measure,
            sales_channel: data.sales_channel,
            video: {
              ...stream.video,
              ...{
                url: data.video_url,
                vimeo_id: data.vimeo_id,
                isRerun: data.is_rerun,
                rerunStartTime: data.rerun_start_time,
              },
            },
          },
        });
      }
      return response.status;
    });
  }

  return (
    <StreamContext.Provider
      value={{
        loading,
        playedSeconds,
        setPlayedSeconds,
        stream,
        currentStream,
        setCurrentStream,
        updateStream,
      }}
    >
      {children}
    </StreamContext.Provider>
  );
};

export const useStreamContext = () => {
  const context = useContext(StreamContext);

  return context;
};
